import { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { useEffectiveTenantId } from "../lib/impersonation";
import { api, callableErrorMessage } from "../lib/api";
import { BoletaImageViewer } from "./BoletaImageViewer";

type Sale = {
  id: string;
  amountFinal: number;
  amountGross: number;
  amountDiscount: number;
  status: string;
  source: string | null;
  cashierUid: string | null;
  cashierEmail: string | null;
  userId: string;
  category: string | null;
  note: string | null;
  discountCode: string | null;
  disputeReason: string | null;
  voucherStoragePath: string | null;
  imageMissing: boolean;
  createdAt: Date | null;
};

type StaffOpt = { uid: string; label: string };

export function SalesTab() {
  const tenantId = useEffectiveTenantId();
  const [from, setFrom] = useState(() => isoDaysAgo(7));
  const [to, setTo] = useState(() => isoDaysAgo(0));
  const [staffFilter, setStaffFilter] = useState<string>("all");
  const [onlyPending, setOnlyPending] = useState(false);
  const [sales, setSales] = useState<Sale[]>([]);
  const [staffOpts, setStaffOpts] = useState<StaffOpt[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(
      query(collection(db, "tenants", tenantId, "users"), where("role", "in", ["staff", "tenant_admin"])),
      (snap) => {
        setStaffOpts(
          snap.docs.map((d) => ({
            uid: d.id,
            label: (d.data().displayName as string) ?? (d.data().email as string) ?? d.id.slice(0, 6)
          }))
        );
      }
    );
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    const fromD = parseLocalDate(from, 0, 0, 0, 0);
    const toD = parseLocalDate(to, 23, 59, 59, 999);
    const q = query(
      collection(db, "tenants", tenantId, "purchases"),
      where("createdAt", ">=", Timestamp.fromDate(fromD)),
      where("createdAt", "<=", Timestamp.fromDate(toD)),
      orderBy("createdAt", "desc")
    );
    return onSnapshot(q, (snap) => {
      setSales(
        snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            amountFinal: data.amountFinal ?? data.amount ?? 0,
            amountGross: data.amountGross ?? data.amount ?? 0,
            amountDiscount: data.amountDiscount ?? 0,
            status: data.status ?? "approved",
            source: data.source ?? null,
            cashierUid: data.cashierUid ?? null,
            cashierEmail: data.cashierEmail ?? null,
            userId: data.userId,
            category: data.category ?? null,
            note: data.note ?? null,
            discountCode: data.discountCode ?? null,
            disputeReason: data.disputeReason ?? null,
            voucherStoragePath: (data.voucherStoragePath as string | undefined) ?? null,
            imageMissing: data.imageMissing === true,
            createdAt: (data.createdAt as Timestamp | undefined)?.toDate() ?? null
          };
        })
      );
    });
  }, [tenantId, from, to]);

  const filtered = useMemo(() => {
    return sales.filter((s) => {
      if (staffFilter !== "all" && s.cashierUid !== staffFilter) return false;
      if (onlyPending && s.status !== "disputed" && s.status !== "pending_admin_review") return false;
      return true;
    });
  }, [sales, staffFilter, onlyPending]);

  const totalDay = filtered.filter((s) => s.status === "approved").reduce((a, s) => a + s.amountFinal, 0);
  const pendingCount = sales.filter((s) => s.status === "disputed" || s.status === "pending_admin_review").length;

  return (
    <div className="flex flex-col gap-3">
      <section className="card flex flex-col gap-3">
        <div className="flex items-end gap-2">
          <label className="flex flex-1 flex-col gap-1 text-[11px] text-leaf-600">
            Desde
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input text-xs" />
          </label>
          <label className="flex flex-1 flex-col gap-1 text-[11px] text-leaf-600">
            Hasta
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input text-xs" />
          </label>
        </div>

        <label className="flex flex-col gap-1 text-[11px] text-leaf-600">
          Staff
          <select
            value={staffFilter}
            onChange={(e) => setStaffFilter(e.target.value)}
            className="input text-sm"
          >
            <option value="all">Todos</option>
            {staffOpts.map((s) => (
              <option key={s.uid} value={s.uid}>
                {s.label}
              </option>
            ))}
          </select>
        </label>

        <button
          onClick={() => setOnlyPending(!onlyPending)}
          className={`rounded-full px-3 py-2 text-xs font-medium transition ${
            onlyPending
              ? "bg-amber-500 text-white"
              : "bg-leaf-50 text-leaf-700 ring-1 ring-leaf-200"
          }`}
        >
          {onlyPending ? "✓ Solo por revisar" : `Por revisar (${pendingCount})`}
        </button>
      </section>

      <section className="card">
        <div className="flex items-baseline justify-between">
          <p className="text-xs uppercase tracking-wider text-leaf-600">Total filtrado</p>
          <p className="font-display text-xl font-semibold text-leaf-950">{formatCLP(totalDay)}</p>
        </div>
        <p className="text-xs text-leaf-500">{filtered.length} ventas</p>
      </section>

      {filtered.length === 0 && (
        <p className="text-center text-sm text-leaf-600">Sin ventas en este rango.</p>
      )}

      {filtered.map((s) => (
        <SaleCard
          key={s.id}
          sale={s}
          staffLabel={staffOpts.find((o) => o.uid === s.cashierUid)?.label}
          open={openId === s.id}
          onToggle={() => setOpenId(openId === s.id ? null : s.id)}
          tenantId={tenantId}
        />
      ))}
    </div>
  );
}

function SaleCard({
  sale,
  staffLabel,
  open,
  onToggle,
  tenantId
}: {
  sale: Sale;
  staffLabel?: string;
  open: boolean;
  onToggle: () => void;
  tenantId: string | null | undefined;
}) {
  return (
    <div className="card">
      <button onClick={onToggle} className="flex w-full items-center justify-between text-left">
        <div className="min-w-0">
          <p className="font-medium text-leaf-950">{formatCLP(sale.amountFinal)}</p>
          <p className="text-[11px] text-leaf-500">
            {sale.createdAt?.toLocaleDateString("es-CL", { day: "2-digit", month: "short" })}{" "}
            {sale.createdAt?.toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}
            {staffLabel && ` · ${staffLabel}`}
            {sale.category && ` · ${sale.category}`}
          </p>
        </div>
        <StatusBadge status={sale.status} />
      </button>

      {open && tenantId && <SaleDetail sale={sale} tenantId={tenantId} />}
    </div>
  );
}

function SaleDetail({ sale, tenantId }: { sale: Sale; tenantId: string }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newAmount, setNewAmount] = useState(String(sale.amountFinal));
  const [reason, setReason] = useState("");

  const submit = async (status?: "approved" | "rejected") => {
    if (reason.trim().length < 4) {
      alert("Escribe un motivo de al menos 4 caracteres.");
      return;
    }
    setBusy(true);
    try {
      await api.adminEditPurchase({
        tenantId,
        purchaseId: sale.id,
        amountFinal: editing ? Number(newAmount) : undefined,
        status,
        reason: reason.trim()
      });
      setEditing(false);
      setReason("");
    } catch (err) {
      alert(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 flex flex-col gap-2 border-t border-leaf-100 pt-3 text-xs text-leaf-700">
      {(sale.voucherStoragePath || sale.imageMissing) && (
        <div className="flex items-center gap-3">
          <BoletaImageViewer
            storagePath={sale.voucherStoragePath}
            imageMissing={sale.imageMissing}
            alt={`Boleta ${sale.id.slice(0, 8)}`}
          />
          <span className="text-xs text-leaf-600">
            {sale.imageMissing ? "Imagen no disponible (legacy)" : "Tap para ver boleta"}
          </span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Field label="Bruto" value={formatCLP(sale.amountGross)} />
        <Field label="Descuento" value={formatCLP(sale.amountDiscount)} />
      </div>
      {sale.discountCode && <Field label="Codigo" value={sale.discountCode} />}
      {sale.source && <Field label="Origen" value={sale.source === "cashier_assisted" ? "Cajero" : "Boleta"} />}
      {sale.note && <Field label="Nota" value={sale.note} />}
      {sale.cashierEmail && <Field label="Cajero" value={sale.cashierEmail} />}
      {sale.disputeReason && (
        <div className="rounded-xl bg-amber-50 px-3 py-2 text-amber-900 ring-1 ring-amber-200">
          <strong>Disputa: </strong>{sale.disputeReason}
        </div>
      )}

      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Motivo del cambio (obligatorio)"
        rows={2}
        className="input text-sm"
      />

      {editing && (
        <label className="flex flex-col gap-1 text-xs">
          Nuevo monto final
          <input
            type="number"
            value={newAmount}
            onChange={(e) => setNewAmount(e.target.value)}
            className="input"
          />
        </label>
      )}

      <div className="grid grid-cols-2 gap-2">
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="rounded-xl bg-leaf-50 px-3 py-2 text-xs font-medium text-leaf-800 ring-1 ring-leaf-200"
          >
            Editar monto
          </button>
        ) : (
          <button
            disabled={busy}
            onClick={() => submit()}
            className="rounded-xl bg-leaf-700 px-3 py-2 text-xs font-medium text-white"
          >
            {busy ? "…" : "Guardar"}
          </button>
        )}
        <button
          disabled={busy}
          onClick={() => submit("approved")}
          className="rounded-xl bg-leaf-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
        >
          Aprobar
        </button>
        <button
          disabled={busy}
          onClick={() => submit("rejected")}
          className="col-span-2 rounded-xl bg-red-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
        >
          Rechazar venta
        </button>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-leaf-50 px-2 py-1.5">
      <p className="text-[10px] uppercase text-leaf-500">{label}</p>
      <p className="text-sm text-leaf-900">{value}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const m: Record<string, { bg: string; fg: string; label: string }> = {
    approved: { bg: "bg-leaf-100", fg: "text-leaf-800", label: "OK" },
    disputed: { bg: "bg-amber-100", fg: "text-amber-800", label: "Disputa" },
    pending_admin_review: { bg: "bg-amber-100", fg: "text-amber-800", label: "Revision" },
    rejected: { bg: "bg-red-100", fg: "text-red-700", label: "Rechazada" },
    undone: { bg: "bg-gray-100", fg: "text-gray-600", label: "Deshecha" }
  };
  const s = m[status] ?? { bg: "bg-gray-100", fg: "text-gray-600", label: status };
  return (
    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${s.bg} ${s.fg}`}>
      {s.label}
    </span>
  );
}

function formatCLP(n: number): string {
  return new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n);
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  // YYYY-MM-DD en hora LOCAL (toISOString convertiria a UTC y puede saltar de dia)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function parseLocalDate(s: string, h: number, m: number, sec: number, ms: number): Date {
  const [y, mo, d] = s.split("-").map(Number);
  return new Date(y, mo - 1, d, h, m, sec, ms);
}
